import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowBack,
    People,
    Phone,
    Email,
    Place,
    Payment,
    Edit,
    Link as LinkIcon,
    Close
} from '@mui/icons-material';
import CashierSidebar from '../../components/cashier/CashierSidebar';
import CashierHeader from '../../components/cashier/CashierHeader';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCustomerById, clientTypeBracket } from '../../services/customerService';
import PaymentChoiceModal from './PaymentChoiceModal';

const CashierCustomerDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { selectedOrganization } = useAuth();
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showPaymentChoice, setShowPaymentChoice] = useState(false);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setLoading(true);
        fetchCustomerById(id)
            .then((data) => {
                if (!cancelled) setCustomer(data?.payload ?? data?.contents ?? data ?? null);
            })
            .catch(() => {
                if (!cancelled) setCustomer(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [id]);

    const phoneDisplay = (c) => (c?.zip ? `${c.zip} ${c.phoneNumber || ''}`.trim() : c?.phoneNumber || c?.phone || '—');
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex">
                <CashierSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
                <div className="flex-1 min-h-screen lg:ml-[var(--cashier-sidebar-w,18rem)] transition-[margin] duration-300 flex items-center justify-center">
                    <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    if (!customer) {
        return (
            <div className="min-h-screen bg-slate-50 flex">
                <CashierSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
                <div className="flex-1 min-h-screen lg:ml-[var(--cashier-sidebar-w,18rem)] transition-[margin] duration-300 flex flex-col items-center justify-center gap-4">
                    <p className="text-slate-600">Client introuvable.</p>
                    <button
                        type="button"
                        onClick={() => navigate('/cashier/customers')}
                        className="px-4 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600"
                    >
                        Retour aux clients
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex">
            <CashierSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden lg:ml-[var(--cashier-sidebar-w,18rem)] transition-[margin] duration-300">
                <CashierHeader onMenuToggle={() => setSidebarOpen(!sidebarOpen)} />

                <main className="min-h-screen px-4 py-4 lg:px-6 lg:py-6 overflow-auto">
                    <div className="w-full space-y-6 mt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => navigate('/cashier/customers')}
                                    className="p-2 rounded-xl hover:bg-white border border-slate-200 text-slate-600 transition-colors"
                                >
                                    <ArrowBack className="w-5 h-5" />
                                </button>
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-md">
                                    <People className="text-white text-xl" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-slate-800">
                                        {customer.name || 'Sans nom'}{' '}
                                        <span className="text-slate-500 font-semibold tabular-nums">{clientTypeBracket(customer)}</span>
                                    </h1>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                            <div className="p-5 sm:p-6 space-y-6">
                                <div>
                                    <h2 className="text-base font-semibold text-slate-800 mb-3">Coordonnées</h2>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3 text-slate-700">
                                            <Phone className="text-slate-400 w-5 h-5 shrink-0" />
                                            <span>{phoneDisplay(customer)}</span>
                                        </div>
                                        {customer.email && (
                                            <div className="flex items-center gap-3 text-slate-700">
                                                <Email className="text-slate-400 w-5 h-5 shrink-0" />
                                                <span>{customer.email}</span>
                                            </div>
                                        )}
                                        {customer.address && (
                                            <div className="flex items-center gap-3 text-slate-700">
                                                <Place className="text-slate-400 w-5 h-5 shrink-0" />
                                                <span>{customer.address}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {(customer.nif || customer.docReference) && (
                                    <div>
                                        <h2 className="text-base font-semibold text-slate-800 mb-3">Informations fiscales</h2>
                                        <div className="space-y-2 text-sm text-slate-700">
                                            {customer.nif && <p>NIF : {customer.nif}</p>}
                                            {customer.docReference && <p>Réf. document : {customer.docReference}</p>}
                                        </div>
                                    </div>
                                )}
                                <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
                                    <button
                                        type="button"
                                        onClick={() => setShowPaymentChoice(true)}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-amber-500 text-white rounded-xl hover:bg-amber-600 font-medium transition-colors"
                                    >
                                        <Payment className="w-5 h-5" />
                                        Paiement
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/cashier/customers', { state: { editCustomer: customer } })}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-slate-100 text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-200 font-medium transition-colors"
                                    >
                                        <Edit className="w-5 h-5" />
                                        Modifier
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            <PaymentChoiceModal
                show={showPaymentChoice}
                onClose={() => setShowPaymentChoice(false)}
                customer={customer}
                onChooseDemande={(c) => {
                    setShowPaymentChoice(false);
                    navigate('/cashier/payment-requests', { state: { customer: c || customer } });
                }}
                onChooseLien={(c) => {
                    setShowPaymentChoice(false);
                    navigate('/cashier/payment-links', { state: { customer: c || customer } });
                }}
            />
        </div>
    );
};

export default CashierCustomerDetailPage;
